import React, { useState, useEffect } from "react";
import { CustomInput, CustomSelect } from "../../../../components";
import indiaStates from "./states.json";

export default function PartnerForm({ initialData, onFieldChange, onBack, onContinue }) {
  const [form, setForm] = useState(initialData);
  const [errors, setErrors] = useState({});

  const targetMarketOptions = [
    "Large Enterprise",
    "Mid-Market",
    "Small Business",
    "Startup",
    "Other",
  ];

  const industryOptions = [
    "Agriculture",
    "Aerospace & Defence",
    "Art, Architecture & Design",
    "Automotive",
    "Banking",
    "Financial Services",
    "Insurance",
    "Education",
    "Energy, Utilities & Resources",
    "Food & Beverages",
    "Government & Public Services",
    "Healthcare",
    "Pharma & Life Sciences",
    "HR & Related Services",
    "IT Hardware & IT Related Services",
    "Manufacturing",
    "Marketing & Advertising",
    "Media & Entertainment",
    "Non-Profit",
    "Professional Services - Non IT",
    "Real Estate & Construction",
    "Retail",
    "E-Commerce",
    "Telecommunications",
    "Transportation & Logistics",
    "Travel & Hospitality",
    "Other",
  ];

  const stateOptions = indiaStates.map((s) => ({
    value: s.name,
    label: s.name,
  }));

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(form);
    }
  }, [form, onFieldChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "employees" && value !== "" && parseInt(value) < 0) {
      setErrors((prev) => ({ ...prev, employees: "Employees cannot be negative" }));
      return;
    }
    // Update form state and clear error.
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Handler for checkbox changes (target market or industry expertise).
  const handleCheckboxChange = (e, field) => {
    const { value, checked } = e.target;
    setForm((prev) => {
      let updated = [...(prev[field] || [])];
      if (checked) {
        // Limit industry expertise to 5 selections.
        if (field === "industry_expertise" && updated.length >= 5) {
          return prev;
        }
        updated = [...updated, value];
      } else {
        updated = updated.filter((i) => i !== value);
      }
      return { ...prev, [field]: updated };
    });
    // Clear field error on change.
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Handler for form submission (continue to next step).
  const handleSubmit = (e) => {
    e.preventDefault();
    // Required fields list.
    const required = [
      "email",
      "company_name",
      "website",
      "first_name",
      "last_name",
      "job_title",
      "business_registration",
      "employees",
      "country",
      "state",
      "city",
      "street_address",
      "zip_code",
      "phone_number",
      "target_market",
      "industry_expertise",
    ];
    // Validate required fields.
    let newErrors = {};
    required.forEach((field) => {
      const value = form[field];
    
      // CASE 1: Array fields (checkbox groups)
      if (Array.isArray(value)) {
        if (value.length === 0) {
          newErrors[field] = "Please select at least one option.";
        }
        return;
      }
    
      // CASE 2: String fields
      if (typeof value === "string") {
        if (value.trim() === "") {
          newErrors[field] = "This field is required";
        }
        return;
      }
    
      // CASE 3: Null / undefined / empty
      if (!value) {
        newErrors[field] = "This field is required";
      }
    });

    // Email validation - must be valid email format
    if (form.email && form.email.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    // Website validation - should not contain @ (not an email)
    if (form.website && form.website.trim() !== "") {
      if (form.website.includes("@")) {
        newErrors.website = "Please enter a valid website URL (not an email address)";
      }
    }
    
    setErrors(newErrors);
    // Proceed if no errors.
    if (Object.keys(newErrors).length === 0) {
      if (onContinue) onContinue(form);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="partner-form">
      {/* Section: Validate Account */}
      <h2 className="apply-title">Validate account</h2>
      <div className="row">
        <div className="col-md-6 py-2 col-12">
          <CustomInput
            label="Email address"
            name="email"
            type="email"
            value={form.email || ""}
            placeholder="Enter email"
            error={errors.email}
            required
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 py-2 col-12">
          <CustomSelect
            label="Data Center"
            name="data_center"
            value={form.data_center || "INDIA"}
            options={[{ value: "INDIA", label: "INDIA" }]}
            isDisabled={true}
          />
        </div>
      </div>

      {/* Section: Company Details */}
      <h2 className="apply-title mt-3">Company details</h2>
      <div className="row">
        <div className="col-md-6 py-2">
          <CustomInput
            label="Company Name"
            name="company_name"
            value={form.company_name || ""}
            error={errors.company_name}
            required
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 py-2">
          <CustomInput
            label="Website"
            name="website"
            value={form.website || ""}
            error={errors.website}
            required
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 py-2">
          <CustomInput
            label="First Name"
            name="first_name"
            value={form.first_name || ""}
            error={errors.first_name}
            required
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 py-2">
          <CustomInput
            label="Last Name"
            name="last_name"
            value={form.last_name || ""}
            error={errors.last_name}
            required
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 py-2">
          <CustomInput
            label="Job Title"
            name="job_title"
            value={form.job_title || ""}
            error={errors.job_title}
            required
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 py-2">
          <CustomInput
            label="Business Registration No."
            name="business_registration"
            value={form.business_registration || ""}
            error={errors.business_registration}
            required
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 py-2">
          <CustomInput
            label="Number of Employees"
            name="employees"
            type="number"
            min="0"
            value={form.employees || ""}
            error={errors.employees}
            required
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Section: Business Location */}
      <h2 className="apply-title mt-3">Business Location</h2>
      <div className="row">
        <div className="col-md-6 py-2">
          <CustomSelect
            label="Country"
            name="country"
            value={form.country || "INDIA"}
            error={errors.country}
            onChange={handleChange}
            options={[{ value: "INDIA", label: "INDIA" }]}
            required
          />
        </div>
        <div className="col-md-6 py-2">
          <CustomSelect
            label="State"
            name="state"
            value={form.state || ""}
            options={stateOptions}
            error={errors.state}
            required
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 py-2">
          <CustomInput
            label="City"
            name="city"
            value={form.city || ""}
            error={errors.city}
            required
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 py-2">
          <CustomInput
            label="Street Address"
            name="street_address"
            value={form.street_address || ""}
            error={errors.street_address}
            required
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 py-2">
          <CustomInput
            label="Zip Code"
            name="zip_code"
            value={form.zip_code || ""}
            error={errors.zip_code}
            required
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 py-2">
          <CustomInput
            label="Phone Number"
            name="phone_number"
            value={form.phone_number || ""}
            error={errors.phone_number}
            required
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Section: Business Expertise */}
      <h2 className="business-expertise-title mt-4">Business Expertise</h2>
      <div className="business-expertise-row">
        <div className="business-col">
          <label className="custom-select-label">
            Target Market <span className="text-danger">*</span>
          </label>
          <div className="checkbox-box">
            {targetMarketOptions.map((item, index) => {
              const id = `t_${index}`;
              return (
                <label key={item} htmlFor={id} className="checkbox-row">
                  <input
                    id={id}
                    type="checkbox"
                    value={item}
                    checked={(form.target_market || []).includes(item)}
                    onChange={(e) => handleCheckboxChange(e, "target_market")}
                  />
                  <span>{item}</span>
                </label>
              );
            })}
          </div>
          {errors.target_market && (
            <div className="custom-select-error">{errors.target_market}</div>
          )}
        </div>
        <div className="business-col">
          <label className="custom-select-label">
            Industry Expertise <span className="text-danger">*</span>
          </label>
          <div className="checkbox-box">
            {industryOptions.map((item, index) => {
              const id = `ind_${index}`;
              return (
                <label key={item} htmlFor={id} className="checkbox-row">
                  <input
                    id={id}
                    type="checkbox"
                    value={item}
                    checked={(form.industry_expertise || []).includes(item)}
                    onChange={(e) => handleCheckboxChange(e, "industry_expertise")}
                  />
                  <span>{item}</span>
                </label>
              );
            })}
          </div>
          {errors.industry_expertise && (
            <div className="custom-select-error">{errors.industry_expertise}</div>
          )}
          <p className="muted-note">You can select a maximum of five industry expertise</p>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="fixed-footer-actions">
        <button className="partner-btn-outline" type="button" onClick={onBack}>
          Back
        </button>
        <button className="partner-btn-primary" type="submit">
          Continue
        </button>
      </div>
    </form>
  );
}